import { useLocation } from 'react-router-dom';

/**
 * SEO Configuration Types
 */
export interface SEOProps {
  /** Page title - will be appended with site name */
  title: string;
  /** Meta description - optimal length is 150-160 characters */
  description: string;
  /** Keywords for the page (comma-separated in meta tag) */
  keywords?: string[];
  /** Open Graph image URL - should be absolute URL in production */
  ogImage?: string;
  /** Canonical URL - defaults to current URL */
  canonical?: string;
  /** Open Graph type - defaults to 'website' */
  type?: 'website' | 'article' | 'product';
  /** Article publication date (for article type) */
  publishedTime?: string;
  /** Article modification date (for article type) */
  modifiedTime?: string;
  /** Article author (for article type) */
  author?: string;
  /** Disable indexing for this page */
  noIndex?: boolean;
  /** Disable following links for this page */
  noFollow?: boolean;
  /** Breadcrumb items for structured data */
  breadcrumbs?: Array<{ name: string; href: string }>;
}

/**
 * Generate breadcrumbs from pathname
 */
function generateBreadcrumbs(pathname: string): Array<{ name: string; href: string }> {
  if (pathname === '/') return [];

  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ name: string; href: string }> = [
    { name: 'Home', href: '/' },
  ];

  // Map of slugs to display names
  const nameMap: Record<string, string> = {
    'features': 'Features',
    'pricing': 'Pricing',
    'demo': 'Request Demo',
    'about': 'About',
    'contact': 'Contact',
    'resources': 'Resources',
    'blog': 'Blog',
    'industries': 'Industries',
    'vs': 'Compare',
    'compare': 'Compare',
    // Industries
    'hvac': 'HVAC',
    'plumbing': 'Plumbing',
    'electrical': 'Electrical',
    'roofing': 'Roofing',
    'landscaping': 'Landscaping',
    'lawn-care': 'Lawn Care',
    'tree-service': 'Tree Service',
    'pest-control': 'Pest Control',
    'garage-doors': 'Garage Doors',
    'carpet-cleaning': 'Cleaning Services',
    'pool-service': 'Pool Service',
    'appliance-repair': 'Appliance Repair',
    'window-cleaning': 'Window Cleaning',
    'pressure-washing': 'Pressure Washing',
    'painting': 'Painting',
    'handyman': 'Handyman',
    // Competitors
    'servicetitan': 'vs ServiceTitan',
    'jobber': 'vs Jobber',
    'housecall-pro': 'vs Housecall Pro',
    'fieldedge': 'vs FieldEdge',
    'service-fusion': 'vs Service Fusion',
  };

  let currentPath = '';
  for (const segment of segments) {
    currentPath += `/${segment}`;
    const name = nameMap[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    breadcrumbs.push({ name, href: currentPath });
  }

  return breadcrumbs;
}

/** Site-wide configuration */
const SITE_CONFIG = {
  name: 'Omniforge',
  tagline: 'The Operating System for Service Companies',
  url: 'https://getomniforge.com',
  defaultOgImage: '/og/default.png',
  twitterHandle: '@omniforge',
  locale: 'en_US',
};

/**
 * Organization structured data for JSON-LD
 */
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Omniforge',
  url: 'https://getomniforge.com',
  logo: 'https://getomniforge.com/images/logo.png',
  description: 'Complete business operating system for service companies. Field service management, scheduling, dispatch, CRM, invoicing, and more.',
  foundingDate: '2019',
  // TODO: Add real social media links when accounts are created
  // sameAs: [
  //   'https://twitter.com/omniforge',
  //   'https://linkedin.com/company/omniforge',
  // ],
  // TODO: Add real phone number
  // contactPoint: {
  //   '@type': 'ContactPoint',
  //   telephone: '+1-XXX-XXX-XXXX',
  //   contactType: 'sales',
  //   availableLanguage: 'English',
  // },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
};

/**
 * Software application structured data for JSON-LD
 */
const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Omniforge',
  url: 'https://getomniforge.com',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, iOS, Android',
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '49',
    highPrice: '99',
    priceCurrency: 'USD',
    offerCount: '3',
    offers: [
      {
        '@type': 'Offer',
        name: 'Starter',
        price: '49',
        priceCurrency: 'USD',
        description: 'For small teams getting started',
      },
      {
        '@type': 'Offer',
        name: 'Professional',
        price: '99',
        priceCurrency: 'USD',
        description: 'For growing service companies',
      },
    ],
  },
  // TODO: Add real aggregate rating when you have verified reviews
  // aggregateRating: {
  //   '@type': 'AggregateRating',
  //   ratingValue: '4.9',
  //   ratingCount: '200',
  //   bestRating: '5',
  // },
  featureList: [
    'Smart Dispatch',
    'Route Optimization',
    'CRM & Pipelines',
    'Invoicing & Payments',
    'Mobile App',
    'AI Receptionist',
    'VoIP Phone System',
    'Fleet Management',
    'HR & Recruiting',
    'Training LMS',
    'Level 10 Meetings',
    'Leader Standard Work',
  ],
};

/**
 * SEO Component - Comprehensive meta tags for search engines and social sharing
 *
 * Uses React 19's built-in document metadata support via special elements
 * that automatically hoist to the document head.
 *
 * @example
 * <SEO
 *   title="Features - Field Service Management Software"
 *   description="23+ integrated modules for service companies."
 *   keywords={['field service', 'dispatch', 'scheduling']}
 *   type="website"
 * />
 */
export function SEO({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
  noFollow = false,
  breadcrumbs,
}: SEOProps) {
  const location = useLocation();

  // Build full title with site name
  const fullTitle = title.includes(SITE_CONFIG.name)
    ? title
    : `${title} | ${SITE_CONFIG.name}`;

  // Build canonical URL
  const canonicalUrl = canonical || `${SITE_CONFIG.url}${location.pathname}`;

  // Build OG image URL
  const ogImageUrl = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${SITE_CONFIG.url}${ogImage}`)
    : `${SITE_CONFIG.url}${SITE_CONFIG.defaultOgImage}`;

  // Build robots directive
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow',
  ].join(', ');

  // Build JSON-LD for the page
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebPage',
    name: fullTitle,
    description,
    url: canonicalUrl,
    ...(type === 'article' && publishedTime && {
      datePublished: publishedTime,
      dateModified: modifiedTime || publishedTime,
      author: author ? { '@type': 'Person', name: author } : undefined,
    }),
    publisher: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_CONFIG.url}/images/logo.png`,
      },
    },
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
  };

  // Build breadcrumb schema if breadcrumbs provided, or auto-generate from path
  const breadcrumbItems = breadcrumbs || generateBreadcrumbs(location.pathname);
  const breadcrumbSchema = breadcrumbItems.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_CONFIG.url}${item.href}`,
    })),
  } : null;

  return (
    <>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />

      {/* Article-specific OG tags */}
      {type === 'article' && publishedTime && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {author && <meta property="article:author" content={author} />}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Additional Meta Tags */}
      <meta name="application-name" content={SITE_CONFIG.name} />
      <meta name="apple-mobile-web-app-title" content={SITE_CONFIG.name} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </>
  );
}

/**
 * SEO data for all marketing site routes
 * This can be used for sitemap generation and consistent SEO across pages
 */
export const seoRoutes = [
  {
    path: '/',
    title: 'Omniforge - The Operating System for Service Companies',
    description: 'Visual management. Real-time command centers. Industry-specific workflows. Omniforge is the complete business operating system for HVAC, plumbing, electrical, landscaping, and more.',
    priority: 1.0,
    changefreq: 'weekly' as const,
  },
  {
    path: '/features',
    title: 'Features - Field Service Management Software',
    description: '23+ integrated modules: dispatch, routes, VoIP, AI receptionist, CRM, pipelines, workflows, task management, job profitability, LMS, and business rhythm tools.',
    priority: 0.9,
    changefreq: 'weekly' as const,
  },
  {
    path: '/pricing',
    title: 'Pricing - Affordable Plans for Every Service Business',
    description: 'Simple, transparent pricing for service companies of all sizes. Start with a 14-day free trial. No credit card required.',
    priority: 0.9,
    changefreq: 'monthly' as const,
  },
  {
    path: '/demo',
    title: 'Request a Demo - See Omniforge in Action',
    description: 'Get a personalized demo tailored to your industry. See how Omniforge can transform your service company operations in 30 minutes.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/about',
    title: 'About Us - Our Mission to Help Service Companies',
    description: 'Omniforge was built by people who ran service companies. We understand the challenges because we lived them.',
    priority: 0.6,
    changefreq: 'monthly' as const,
  },
  {
    path: '/contact',
    title: 'Contact Us - Get in Touch',
    description: 'Get in touch with the Omniforge team. We are here to help with sales questions, support, partnerships, and more.',
    priority: 0.6,
    changefreq: 'monthly' as const,
  },
  {
    path: '/resources',
    title: 'Resources - Guides, Case Studies & Webinars',
    description: 'Free resources to help grow your service business. Download guides, read case studies, and watch webinars on dispatch, operations, and scaling.',
    priority: 0.7,
    changefreq: 'weekly' as const,
  },
  {
    path: '/blog',
    title: 'Blog - Tips for Growing Your Service Business',
    description: 'Expert insights on growing and running a successful service business. Tips on operations, technology, growth strategies, and industry trends.',
    priority: 0.7,
    changefreq: 'daily' as const,
  },
  // Industry Pages
  {
    path: '/industries/hvac',
    title: 'HVAC Software - Scheduling, Dispatch & Management',
    description: 'HVAC software built for pros. Smart dispatch, equipment tracking, maintenance agreements, and mobile app. Start your free trial.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/plumbing',
    title: 'Plumbing Software - Scheduling & Dispatch',
    description: 'Plumbing software for emergency dispatch, job management, and invoicing. Built for plumbing contractors who want to grow.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/electrical',
    title: 'Electrical Contractor Software - Management & Scheduling',
    description: 'Electrical contractor software for project tracking, inspections, and team management. Streamline your electrical business.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/garage-doors',
    title: 'Garage Door Software - Service & Dispatch',
    description: 'Garage door service software with parts inventory, emergency dispatch, and mobile access. Perfect for garage door companies.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/landscaping',
    title: 'Landscaping Software - Scheduling & Route Optimization',
    description: 'Landscaping business software with route optimization, seasonal contracts, and crew management. Grow your landscaping company.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/pest-control',
    title: 'Pest Control Software - Routes & Compliance',
    description: 'Pest control software with recurring routes, compliance tracking, and customer management. Built for pest control businesses.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/roofing',
    title: 'Roofing Software - Project & Crew Management',
    description: 'Roofing contractor software with storm tracking, crew scheduling, and job costing. Manage your roofing business efficiently.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/carpet-cleaning',
    title: 'Carpet Cleaning Software - Scheduling & Pricing',
    description: 'Carpet cleaning software with room-based pricing, scheduling, and upsells. Grow your cleaning business.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/pool-service',
    title: 'Pool Service Software - Routes & Chemical Tracking',
    description: 'Pool service software with chemical tracking, maintenance routes, and customer management. Perfect for pool companies.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/appliance-repair',
    title: 'Appliance Repair Software - Parts & Warranty Tracking',
    description: 'Appliance repair software with parts ordering, warranty tracking, and scheduling. Streamline your repair business.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/lawn-care',
    title: 'Lawn Care Software - Scheduling & Route Optimization',
    description: 'Lawn care software for mowing companies. Route optimization, recurring schedules, weather rescheduling, and crew tracking.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/tree-service',
    title: 'Tree Service Software - Estimating & Equipment Tracking',
    description: 'Tree service software for arborists. Visual estimating, equipment tracking, multi-day scheduling, and storm dispatch.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/window-cleaning',
    title: 'Window Cleaning Software - Routing & Scheduling',
    description: 'Window cleaning software for residential and commercial cleaners. Route optimization, recurring schedules, and commercial billing.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/pressure-washing',
    title: 'Pressure Washing Software - Estimating & Scheduling',
    description: 'Pressure washing software for power wash companies. Satellite estimating, equipment tracking, and recurring service plans.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/painting',
    title: 'Painting Contractor Software - Estimating & Project Management',
    description: 'Painting contractor software for residential and commercial painters. Estimating, project scheduling, material tracking, and crew management.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
  {
    path: '/industries/handyman',
    title: 'Handyman Software - Job Management & Scheduling',
    description: 'Handyman business software for home repair pros. Multi-skill dispatching, task pricing, and customer retention tools.',
    priority: 0.8,
    changefreq: 'monthly' as const,
  },
];

export default SEO;
